import { Line, Text } from "@react-three/drei";
import type { SideName } from "@tosize/shapes/src/lib/models/Side/SideName";
import type { ThreeSidePathsType } from "@tosize/shapes/src/types/three";
import { formatNumber } from "@tosize/ui";
import { useMemo } from "react";
import { type Curve, EllipseCurve, LineCurve, Path, type Vector2, Vector3 } from "three";
import { match } from "ts-pattern";
import type { PanelVisuProps } from "../..";
import { usePanelVisualisation } from "../../Context";
import { RENDER_QUALITY } from "../generic-shape/Shape";
import {
	ACTIVE_LINE_COLOR,
	ENDLINE_LENGTH,
	LINE_COLOR,
	LINE_WIDTH,
	OFFSET,
	TEXT_COLOR,
	addPathEndlines,
	getLinePosition,
	getOuterDimensions,
	getPoints,
	isActiveLine,
} from "./utils";

interface SideLineProps {
	side: ThreeSidePathsType;
	finishInputs?: PanelVisuProps["finishInputs"];
	fontSize: number;
	currentField?: string;
}

export function SideLine({ side, finishInputs, fontSize, currentField }: SideLineProps) {
	const { lang, shapeModel, dimensions, isModified, sidePaths, dimensionsString } =
		usePanelVisualisation();
	const outerDimensions = useMemo(
		() => (sidePaths ? getOuterDimensions(sidePaths) : undefined),
		[sidePaths],
	);
	const points = useMemo(() => getPoints(side.path), [side]);
	const length = useMemo(() => side.path.getLength(), [side]);

	const lineType = useMemo(() => {
		const { firstPoint, middlePoint, lastPoint } = points;
		if (firstPoint.x === lastPoint.x && firstPoint.y === lastPoint.y) {
			return "closed";
		}

		if (side.path.curves[0] instanceof EllipseCurve) {
			return "arc";
		}

		if (
			side.path.curves[0] instanceof LineCurve &&
			+firstPoint.y === +lastPoint.y &&
			+lastPoint.y === +middlePoint.y
		) {
			return "horizontal";
		}
		if (+firstPoint.x === +lastPoint.x) {
			return "vertical";
		}

		const xDiff = Math.max(firstPoint.x, lastPoint.x) - Math.min(firstPoint.x, lastPoint.x);
		const yDiff = Math.max(firstPoint.y, lastPoint.y) - Math.min(firstPoint.y, lastPoint.y);

		if (xDiff > yDiff) {
			return "angledHorizontal";
		}

		if (yDiff > xDiff) {
			return "angledVertical";
		}

		return "angled";
	}, [side, points]);

	const linePosition = useMemo(() => getLinePosition(points), [points]);

	const groupPosition = useMemo(() => {
		return match(lineType)
			.with("angledHorizontal", "angledVertical", "angled", () => {
				return new Vector3(points.firstPoint.x, points.firstPoint.y, 0);
			})
			.otherwise(() => {
				return new Vector3(outerDimensions?.smallestX, outerDimensions?.smallestY, 0);
			});
	}, [outerDimensions, lineType, points]);

	const groupAngle: number = useMemo(() => {
		return match(lineType)
			.returnType<number>()
			.with("angledHorizontal", "angledVertical", "angled", () => {
				// Calculate the vector representing the direction of the line
				const directionVector = points.lastPoint.clone().sub(points.firstPoint);

				// Calculate the angle relative to the horizontal axis (x-axis)
				return Math.atan2(directionVector.y, directionVector.x) - Math.PI / 2;
			})
			.otherwise(() => {
				return 0;
			});
	}, [lineType, points]);

	const linePoints = useMemo(() => {
		const originalPath = side.path.clone();
		const path = match(lineType)
			.returnType<Path>()
			.with("closed", () => {
				const biggestX = Math.max(...side.path.getPoints(RENDER_QUALITY).map((point) => point.x));
				const biggestY = Math.max(...side.path.getPoints(RENDER_QUALITY).map((point) => point.y));
				const path = side.path.clone();

				path.curves.map((curve) => {
					const index = path.curves.indexOf(curve);
					const previousCurve = path.curves[index - 1] ?? path.curves[path.curves.length - 1];
					const original = originalPath.curves[index]! as LineCurve;
					const nextCurve =
						path.curves[index + 1] === undefined
							? originalPath.curves[0] // get first curve
							: originalPath.curves[index + 1];

					if (curve instanceof EllipseCurve) {
						curve.xRadius += OFFSET;
						curve.yRadius += OFFSET;
					}

					if (curve instanceof LineCurve) {
						// Check if next curve is a line and if it is connected to this line
						const isHorizontal = original.v1.y === original.v2.y;

						if (nextCurve instanceof LineCurve) {
							if (curve.v2.x === nextCurve.v1.x && curve.v2.y === nextCurve.v1.y) {
								if (isHorizontal) {
									if (curve.v2.x > curve.v1.x) {
										curve.v2.x += OFFSET;
									} else {
										curve.v2.x -= OFFSET;
									}
								} else {
									if (curve.v2.y > curve.v1.y) {
										curve.v2.y += OFFSET;
									} else {
										curve.v2.y -= OFFSET;
									}
								}
							}
						}

						if (index === 0 && previousCurve instanceof LineCurve) {
							curve.v1.x -= OFFSET;
						}

						if (isHorizontal) {
							const isTopLine = curve.v1.y < biggestY / 2;

							curve.v1.y += isTopLine ? -OFFSET : OFFSET;
							curve.v2.y += isTopLine ? -OFFSET : OFFSET;

							if (curve.v2.x === outerDimensions?.biggestX) {
								curve.v2.x += OFFSET;
							} else if (curve.v1.x === outerDimensions?.biggestX) {
								curve.v1.x += OFFSET;
							} else if (curve.v1.x === outerDimensions?.smallestX) {
								curve.v1.x -= OFFSET - ENDLINE_LENGTH;
							} else if (curve.v2.x === outerDimensions?.smallestX) {
								curve.v2.x -= OFFSET;
							}
						}
						if (!isHorizontal) {
							const isLeftLine = curve.v1.x < biggestX / 2;

							curve.v1.x += isLeftLine ? -OFFSET : OFFSET;
							curve.v2.x += isLeftLine ? -OFFSET : OFFSET;
						}
					}
				});

				return path;
			})
			.with("arc", () => {
				const path = side.path.clone();
				path.curves.map((curve) => {
					if (curve instanceof EllipseCurve) {
						curve.xRadius += OFFSET;
						curve.yRadius += OFFSET;
					}
				});
				return path;
			})
			.with("horizontal", () => {
				const path = side.path.clone();
				const lineOffset =
					points.firstPoint.y < Number(outerDimensions?.biggestY) / 2 ? -OFFSET : OFFSET;

				path.curves.map((curve: Curve<Vector2>) => {
					if (curve instanceof LineCurve) {
						curve.v1.y += lineOffset;
						curve.v2.y += lineOffset;
					}
				});

				return path;
			})
			.with("vertical", () => {
				const path = side.path.clone();
				const lineOffset =
					points.firstPoint.x < Number(outerDimensions?.biggestX) / 2 ? -OFFSET : OFFSET;

				path.curves.map((curve: Curve<Vector2>) => {
					if (curve instanceof LineCurve) {
						curve.v1.x += lineOffset;
						curve.v2.x += lineOffset;
					}
				});
				return path;
			})
			.with("angledHorizontal", "angledVertical", "angled", () => {
				const path = new Path().moveTo(OFFSET, 0).lineTo(OFFSET, length);

				return path;
			})
			.exhaustive();

		return addPathEndlines(path, ENDLINE_LENGTH).getPoints(RENDER_QUALITY);
	}, [lineType, points, side, outerDimensions, length]);

	const textPosition = useMemo(() => {
		return match(lineType)
			.with("closed", () => {
				const minX = Math.min(...side.path.getPoints(RENDER_QUALITY).map((point) => point.x));
				const maxX = Math.max(...side.path.getPoints(RENDER_QUALITY).map((point) => point.x));
				const length = maxX - minX;
				return new Vector3(length / 2, -OFFSET * 2, 0);
			})
			.with("arc", () => {
				return match(linePosition)
					.with(
						"leftTop",
						() =>
							new Vector3(
								points.middlePoint.x - OFFSET * 1.5,
								points.middlePoint.y + OFFSET * 1.5,
								0,
							),
					)
					.with(
						"leftBottom",
						() =>
							new Vector3(
								points.middlePoint.x - OFFSET * 1.5,
								points.middlePoint.y - OFFSET * 1.5,
								0,
							),
					)
					.with(
						"rightTop",
						() =>
							new Vector3(
								points.middlePoint.x + OFFSET * 1.5,
								points.middlePoint.y + OFFSET * 1.5,
								0,
							),
					)
					.with(
						"rightBottom",
						() =>
							new Vector3(
								points.middlePoint.x + OFFSET * 1.5,
								points.middlePoint.y - OFFSET * 1.5,
								0,
							),
					)
					.otherwise(() => new Vector3(points.middlePoint.x, points.middlePoint.y + OFFSET * 2, 0));
			})
			.with("horizontal", () => {
				return new Vector3(
					Math.min(points.firstPoint.x, points.lastPoint.x) + length / 2,
					points.firstPoint.y + (points.firstPoint.y > 0 ? OFFSET * 2 : -OFFSET * 2),
					0,
				);
			})
			.with("vertical", () => {
				return new Vector3(
					points.firstPoint.x > 0
						? points.firstPoint.x + OFFSET * 2
						: points.firstPoint.x - OFFSET * 2,
					Math.min(points.firstPoint.y, points.lastPoint.y) + length / 2,
					0,
				);
			})
			.with("angledHorizontal", "angledVertical", "angled", () => {
				return new Vector3(
					OFFSET * 2,
					length / 2,
					// Math.min(points.firstPoint.y, points.lastPoint.y) + length / 2,
					0,
				);
			})
			.exhaustive();
	}, [
		length,
		linePosition,
		lineType,
		points.firstPoint.x,
		points.firstPoint.y,
		points.lastPoint.x,
		points.lastPoint.y,
		points.middlePoint.x,
		points.middlePoint.y,
		side.path,
	]);

	const textRotation = useMemo(() => {
		return match(lineType)
			.returnType<number>()
			.with("arc", () => {
				return match(linePosition)
					.returnType<number>()
					.with("leftTop", "rightBottom", () => Math.PI / 4)
					.with("leftBottom", () => -Math.PI / 4)
					.with("rightTop", () => -Math.PI / 4)
					.otherwise(() => 0);
			})
			.with("vertical", () => {
				return Math.PI / 2;
			})
			.with("angledHorizontal", () => {
				return match(linePosition)
					.returnType<number>()
					.with("rightBottom", () => Math.PI / 2)
					.otherwise(() => -Math.PI / 2);
			})
			.with("angledVertical", "angled", () => {
				return -Math.PI / 2;
			})
			.otherwise(() => 0);
	}, [linePosition, lineType]);

	const sideLengthString = useMemo(() => {
		if (!isModified) return undefined;

		const sideLength =
			side && dimensions
				? shapeModel?.calculateSideLength(side.code as SideName, dimensions)
				: undefined;

		if (dimensionsString && (side.code === "top" || side.code === "bottom")) {
			return dimensionsString;
		}

		return sideLength !== undefined
			? formatNumber({
					lang,
					value: sideLength / 10,
					style: "unit",
					unit: "centimeter",
				})
			: undefined;
	}, [dimensions, dimensionsString, isModified, lang, shapeModel, side]);

	const label = useMemo(() => {
		return finishInputs?.find((input) => side.groupCodes?.includes(input.code))?.name;
	}, [finishInputs, side.groupCodes]);

	const text = [sideLengthString, label].filter(Boolean).join(" - ");

	return (
		<group position={groupPosition}>
			<group rotation={[0, 0, groupAngle]}>
				<Line
					points={linePoints}
					lineWidth={LINE_WIDTH}
					color={isActiveLine(side.code, currentField) ? ACTIVE_LINE_COLOR : LINE_COLOR}
				/>

				<Text
					fontSize={fontSize}
					anchorX="center"
					position={textPosition}
					rotation={[0, 0, textRotation]}
					color={TEXT_COLOR}
				>
					{text}
				</Text>
			</group>
		</group>
	);
}
